import { Injectable } from '@angular/core';
import {
    BACKSPACE,
    DELETE,
    END,
    ESCAPE,
    KEY_A,
    KEY_C,
    KEY_V,
    KEY_X,
    RIGHT_ARROW,
    TAB,
} from '@sb-tools/typescript/constants/keycodes';

const escapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '`': '&#x60;',
};
const escapeRegex = /[&<>"'`]/g;

@Injectable({ providedIn: 'root' })
export class HelperService {
    static escapeHtml(text: string): string {
        if (text == null) {
            return '';
        }
        return String(text).replace(escapeRegex, (char) => escapeMap[char]);
    }
    static readonly domainValidationPattern = '^[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,6}$';

    static readonly emailValidationPattern =
        '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';

    static readonly iframeSupportedFileTypesList = ['image', 'pdf', 'txt', 'code'];

    static readonly msOfficeFileTypesList = ['word', 'powerpoint', 'excel'];

    static readonly urlValidationPattern =
        /^(http(s)?:\/\/)?(www\.)?[a-zA-Z0-9@:%._\-+~#=]{1,256}\.[a-z]{2,6}\b((\/)([a-zA-Z0-9@:%_\-+.~#?&=](\/)?)*)?$/;

    constructor() {}

    static checkIsInIframe(): boolean {
        return window.location !== window.parent.location;
    }

    static compareStringArrays(firstArray: string[], secondArray: string[]): boolean {
        const firstArrayProcessed = firstArray.map((item) => {
            return item.toLowerCase();
        });

        const secondArrayProcessed = secondArray.map((item) => {
            return item.toLowerCase();
        });

        const diffFirstToSecond = firstArrayProcessed.filter((item) => !secondArrayProcessed.includes(item));
        const diffSecondToFirst = secondArrayProcessed.filter((item) => !firstArrayProcessed.includes(item));
        const isEqual = diffFirstToSecond.length === 0 && diffSecondToFirst.length === 0;

        return isEqual;
    }

    static getFileExtension(fileName: string): string {
        const dotIndex = fileName?.lastIndexOf('.');

        if (dotIndex > -1) {
            let extension = fileName.substr(dotIndex + 1);
            if (extension) {
                extension = extension.split('&')[0];
                extension = extension.split('?')[0];
                return extension.toLowerCase();
            }
        }

        return '';
    }

    static getFileType(fileExtension: string): string {
        switch (fileExtension) {
            case 'jpeg':
            case 'jpg':
            case 'png':
            case 'gif':
            case 'jp2':
            case 'jpx':
            case 'j2k':
            case 'j2c':
            case 'jif':
            case 'jfif':
            case 'php':
                return 'image';
            case 'pdf':
                return 'pdf';
            case '3g2':
            case '3gp':
            case 'aaf':
            case 'asf':
            case 'avchd':
            case 'avi':
            case 'drc':
            case 'flv':
            case 'm2v':
            case 'm4p':
            case 'm4v':
            case 'mkv':
            case 'mng':
            case 'mov':
            case 'mp2':
            case 'mp4':
            case 'mpe':
            case 'mpeg':
            case 'mpg':
            case 'mpv':
            case 'mxf':
            case 'nsv':
            case 'ogg':
            case 'ogv':
            case 'qt':
            case 'rm':
            case 'rmvb':
            case 'roq':
            case 'svi':
            case 'vob':
            case 'webm':
            case 'wmv':
            case 'f4v':
            case 'yuv':
                return 'video';
            case 'txt':
                return 'txt';
            case 'doc':
            case 'dot':
            case 'docx':
            case 'docm':
            case 'dotx':
            case 'dotm':
            case 'docb':
                return 'word';
            case 'xls':
            case 'xlt':
            case 'xlm':
            case 'xlsx':
            case 'xlsm':
            case 'xltx':
            case 'xltm':
                return 'excel';
            case 'ppt':
            case 'pot':
            case 'pps':
            case 'pptx':
                return 'powerpoint';
            case 'html':
            case 'js':
            case 'json':
            case 'css':
                return 'code';
            default:
                return 'file';
        }
    }

    static getFileTypeByMimeType(mimeType = ''): string {
        if (mimeType.includes('video')) {
            return 'video';
        } else if (mimeType.includes('image')) {
            return 'image';
        } else {
            return 'file';
        }
    }

    static getParameterByName(name, isCaseSensitive?) {
        let locationSearch = location.search;
        if (!isCaseSensitive) {
            name = name.toLowerCase();
            locationSearch = locationSearch.toLowerCase();
        }

        name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
        const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        const results = regex.exec(locationSearch);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    static getValueFromHash(hash: string, name: string): string {
        name = name.replace(/[/, '\\[').replace(/[\]]/, '\\]');

        const regex = new RegExp('[\\?&|#]' + name + '=([^&#]*)');
        const results = regex.exec(hash);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    static isEmailValid(email: string): boolean {
        if (email) {
            email = email.trim();

            const emailRegExp =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

            return emailRegExp.test(email);
        }

        return false;
    }

    // todo: move file methods to separated fileHelperService
    static isImage(extension) {
        if (extension) {
            return !![
                'jpeg',
                'jpg',
                'png',
                'gif',
                'gif',
                'jp2',
                'jpx',
                'j2k',
                'j2c',
                'jif',
                'jfif',
                'php', // facebook send some images as php
            ].find((item) => {
                return item === extension;
            });
        } else {
            return false;
        }
    }

    // todo: move to form elements module?
    static isKeyAllowedForNumberField(event) {
        if (
            event.keyCode === DELETE ||
            event.keyCode === BACKSPACE ||
            event.keyCode === TAB ||
            event.keyCode === ESCAPE ||
            // Allow: Ctrl+A
            (event.keyCode === KEY_A && (event.ctrlKey || event.metaKey)) ||
            // Allow: Ctrl+V
            ((event.ctrlKey || event.metaKey) && event.which === KEY_V) ||
            // Allow: Ctrl+c
            ((event.ctrlKey || event.metaKey) && event.which === KEY_C) ||
            // Allow: Ctrl+x
            ((event.ctrlKey || event.metaKey) && event.which === KEY_X) ||
            // Allow: home, end, left, right
            (event.keyCode >= END && event.keyCode <= RIGHT_ARROW)
        ) {
            return true;
        } else {
            return false;
        }
    }

    static isLocalStorageNameSupported() {
        try {
            const testKey = 'test';
            const storage = window.sessionStorage;

            storage.setItem(testKey, '1');
            storage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }

    static isMobileOrTabletDevice(): boolean | undefined {
        if (typeof document === 'undefined') {
            return undefined;
        }
        let check = false;
        ((a) => {
            if (
                /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
                    a,
                ) ||
                /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
                    a.substr(0, 4),
                )
            ) {
                check = true;
            }
        })(navigator.userAgent || navigator.vendor || (window as any).opera);
        return check;
    }

    static parseFromFbSmileys(text, isFb) {
        if (isFb) {
            if (text) {
                return text
                    .replace(/8-\)/g, '\uD83D\uDE0E')
                    .replace(/3:\)/g, '\uD83D\uDE08')
                    .replace(/:\*/g, '\uD83D\uDE19')
                    .replace(/:P/g, '\uD83D\uDE0B')
                    .replace(/:\\/g, '\uD83D\uDE15')
                    .replace(/:\(/g, '\uD83D\uDE1F')
                    .replace(/-_-/g, '😑')
                    .replace(/:O/g, '😦')
                    .replace(/\(y\)/g, '\uD83D\uDC4D');
            } else {
                return '';
            }
        }
        return text;
    }

    // todo: check fb smiles
    static parseToFbSmileys(text, isFb) {
        if (isFb) {
            if (text) {
                return text
                    .replace(new RegExp('\uD83D\uDE0E', 'g'), '8-)')
                    .replace(new RegExp('\uD83D\uDE08', 'g'), '3:)')
                    .replace(new RegExp('\uD83D\uDE19', 'g'), ':*')
                    .replace(new RegExp('\uD83D\uDE0B', 'g'), ':P')
                    .replace(new RegExp('\uD83D\uDE15', 'g'), ':\\')
                    .replace(new RegExp('\uD83D\uDE1F', 'g'), ':(')
                    .replace(new RegExp('😑', 'g'), '-_-')
                    .replace(new RegExp('😦', 'g'), ':O')
                    .replace(new RegExp('\uD83D\uDC4D', 'g'), '(y)');
            } else {
                return '';
            }
        }
        return text;
    }

    static placeCaretAtStart(textarea) {
        const node = textarea[0];
        if (!node) {
            return;
        }
        node.focus();
        const textNode = node.firstChild || node;
        const caret = 0; // insert caret after the 10th character say
        const range = document.createRange();
        range.setStart(textNode, caret);
        range.setEnd(textNode, caret);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    }

    static strip(html, isHtml?) {
        if (isHtml) {
            return html;
        }

        let result = '';

        try {
            result = html
                .replace(/<br(\s*)\/*>(\s*)<div/gi, '$2<div')
                .replace(/<br(\s*)\/*>(\s*)<p/gi, '$2<p')
                .replace(/<div(\s*)\/*>(\s*)<br(\s*)\/*>/gi, '<div>$2')
                .replace(/<p(\s*)\/*>(\s*)<br(\s*)\/*>/gi, '<p>$2')
                .replace(/<br(\s*)\/*>/gi, '\n') // replace single line-breaks
                .replace(/(<[p|div])/gi, '\n$1') // add a line break before all div and p tags
                .replace(/<style(.|\s)*?\/style>/gi, '')
                .replace(/<script(.|\s)*?\/script>/gi, '')
                .replace(/(<([^>]+)>)/gi, ''); // remove any remaining tags

            // result = result.replace(/\r?\n|\r/, '').trim()  - I dont understand why it was added, I replaced it to simple trim();
            result = result.trim();
        } catch {}

        const tempTextarea = document.createElement('textarea');
        tempTextarea.innerHTML = result;
        result = tempTextarea.value;
        tempTextarea.remove();
        return result;
    }
}
